/**
 * Bosnian strings for the profile screens.
 *
 * The base every other language is measured against. Enum labels are NOT
 * here and must never be — statuses, types, priorities, roles and months
 * come from /api/meta, and a second copy is the drift that put IN_PROGRESS
 * on a manager's badge in the legacy app.
 *
 * The namespace covers the account itself and every way back into one: the
 * profile page, the two password-recovery screens, the forced first password,
 * and the page a scanned card lands on. They share a vocabulary — card,
 * activation code, supervisor — and splitting them would mean translating that
 * vocabulary twice.
 */

export const profile = {
  // ---- The page, and words it shares with the screens below --------
  'profile.title': 'Moj nalog',
  'profile.subtitle': 'Kontakt podaci, lozinka i zaštita naloga.',
  'profile.attribution': 'KaProLean — softver:',
  'profile.cancel': 'Odustani',
  'profile.backToLogin': 'Nazad na prijavu',

  // Shown after the link in the verification mail redirects back here.
  'profile.verified.success': 'E-mail adresa je potvrđena.',
  'profile.verified.failed': 'Link za potvrdu nije važeći ili je istekao.',

  // ---- Identity ----------------------------------------------------
  // Row labels only. The role itself is `user.roleLabel` from the server.
  'profile.identity.title': 'Podaci o zaposlenom',
  'profile.identity.name': 'Ime i prezime',
  'profile.identity.employeeCode': 'Šifra zaposlenog',
  'profile.identity.role': 'Uloga',
  'profile.identity.plant': 'Pogon',
  'profile.identity.lastLogin': 'Posljednja prijava',
  'profile.identity.note':
    'Ove podatke mijenja administrator. Ako nešto nije tačno, javite rukovodiocu.',

  // ---- Contact -----------------------------------------------------
  'profile.contact.title': 'Kontakt',
  'profile.contact.email': 'E-pošta',
  'profile.contact.email.hint':
    'Nije obavezna. Većina radnika je nema — obavještenja u aplikaciji stižu uvijek.',
  'profile.contact.email.placeholder': 'ime.prezime@firma.com',
  'profile.contact.verified': 'Adresa je potvrđena',
  'profile.contact.unverified': 'Adresa nije potvrđena',
  'profile.contact.sendLink': 'Pošalji link',
  'profile.contact.sendFailed': 'Slanje nije uspjelo.',
  'profile.contact.phone': 'Telefon',
  'profile.contact.phone.placeholder': 'npr. 063 123 456',
  'profile.contact.notify': 'Šalji obavještenja i na e-poštu',
  'profile.contact.notify.hint': 'Vrijedi samo za potvrđenu adresu.',
  'profile.contact.save': 'Sačuvaj',

  // ---- My own QR card ----------------------------------------------
  // Reprinting *is* reissuing — the token is not retrievable — so both
  // confirmations say out loud that the card in the pocket dies.
  'profile.badge.title': 'Moja QR kartica',
  'profile.badge.intro':
    'Umjesto kucanja šifre i lozinke, skenirajte svoju karticu telefonom i prijava je gotova. Korisno kad stojite pored mašine, sa rukavicama.',
  'profile.badge.none': 'Još nemate izdatu karticu.',
  /*
   * Two sentences for one state, because the issue date is optional and a
   * date glued onto the end of a translated sentence lands in the wrong
   * place in half the languages here.
   */
  'profile.badge.issued':
    'Kartica vam je već izdata. Kod se ne može ponovo prikazati; ako vam treba nova, stara prestaje da važi.',
  'profile.badge.issuedAt':
    'Kartica vam je već izdata — {date}. Kod se ne može ponovo prikazati; ako vam treba nova, stara prestaje da važi.',
  'profile.badge.managerWarning':
    'Vaš nalog ima proširena prava. Ako izdajete karticu, uključite i dvofaktorsku zaštitu — kartica sama nije dovoljna brana za ovakav nalog.',
  'profile.badge.issue': 'Izdaj karticu',
  'profile.badge.issueNew': 'Izdaj novu karticu',
  'profile.badge.revoke': 'Poništi karticu',
  // `\n\n` because these two go through window.confirm, which has no markup.
  'profile.badge.reissueConfirm':
    'Već imate izdatu karticu.\n\nIzdavanjem nove, kartica koju sada nosite prestaje da važi odmah. Nastaviti?',
  'profile.badge.revokeConfirm':
    'Poništiti karticu?\n\nKartica odmah prestaje da važi. Prijava šifrom i lozinkom i dalje radi.',
  'profile.badge.issueFailed': 'Izdavanje nije uspjelo.',
  'profile.badge.revokeFailed': 'Poništavanje nije uspjelo.',
  'profile.badge.printHint':
    'Odštampajte je sada i plastificirajte. Kod se prikazuje samo ovaj put — ako zatvorite stranicu prije štampe, izdajte novu karticu.',
  'profile.badge.print': 'Štampaj karticu',
  'profile.badge.done': 'Gotovo',

  // ---- Password ----------------------------------------------------
  // `new`, `new.hint`, `confirm` and `mismatch` are shared with the reset
  // and first-password screens, which ask for exactly the same thing.
  'profile.password.title': 'Promjena lozinke',
  'profile.password.current': 'Trenutna lozinka',
  'profile.password.new': 'Nova lozinka',
  'profile.password.new.hint': 'Najmanje 8 znakova.',
  'profile.password.confirm': 'Potvrda lozinke',
  'profile.password.confirmNew': 'Potvrda nove lozinke',
  'profile.password.mismatch': 'Lozinke se ne poklapaju.',
  'profile.password.otherSessionsNote':
    'Nakon promjene, sve druge prijave na vaš nalog biće prekinute — ova ostaje aktivna.',
  'profile.password.submit': 'Promijeni lozinku',

  // ---- Two-factor --------------------------------------------------
  'profile.twoFactor.title': 'Dvofaktorska zaštita',
  'profile.twoFactor.on': 'Uključeno',
  'profile.twoFactor.off': 'Isključeno',
  'profile.twoFactor.description':
    'Uz lozinku traži i šestocifreni kod iz aplikacije na telefonu (Google Authenticator, Microsoft Authenticator, Aegis…). Preporučeno za rukovodioce i administratore.',
  'profile.twoFactor.enable': 'Uključi zaštitu',
  'profile.twoFactor.disable': 'Isključi',
  'profile.twoFactor.newRecoveryCodes': 'Novi rezervni kodovi',
  'profile.twoFactor.beginFailed': 'Pokretanje nije uspjelo.',
  'profile.twoFactor.disableTitle': 'Isključiti dvofaktorsku zaštitu?',
  'profile.twoFactor.disableExplain':
    'Nalog će ubuduće biti zaštićen samo lozinkom. Potvrdite lozinkom da to zaista želite.',
  'profile.twoFactor.password': 'Lozinka',

  'profile.twoFactor.setup.title': 'Uključivanje dvofaktorske zaštite',
  'profile.twoFactor.setup.confirm': 'Potvrdi',
  'profile.twoFactor.setup.step1': '1. Skenirajte kod',
  'profile.twoFactor.setup.step1.hint':
    'Otvorite aplikaciju za kodove na telefonu i skenirajte:',
  'profile.twoFactor.setup.qrAlt': 'QR kod za dvofaktorsku zaštitu',
  'profile.twoFactor.setup.step2': '2. Ili unesite ključ ručno',
  'profile.twoFactor.setup.step2.hint': 'Ako kamera ne može da uhvati kod:',
  'profile.twoFactor.setup.step3': '3. Unesite kod iz aplikacije',
  'profile.twoFactor.setup.code': 'Šestocifreni kod',

  // ---- Recovery codes ----------------------------------------------
  'profile.recovery.title': 'Rezervni kodovi',
  'profile.recovery.intro':
    'Sačuvajte ove kodove na sigurno mjesto — svaki vrijedi jednom i služi da uđete u nalog ako izgubite telefon.',
  'profile.recovery.note':
    'Kodove možete ponovo pogledati na ovoj stranici dok ste prijavljeni.',
  'profile.recovery.copy': 'Kopiraj kodove',
  'profile.recovery.copied': 'Kodovi su kopirani.',

  // ---- Signed-in devices -------------------------------------------
  'profile.sessions.title': 'Prijavljeni uređaji',
  'profile.sessions.note':
    'Možete biti prijavljeni na više uređaja istovremeno. Ako ste izgubili telefon ili ste ostali prijavljeni na zajedničkom računaru, odjavite taj uređaj ovdje.',
  'profile.sessions.revokeOthers': 'Odjavi ostale',
  'profile.sessions.loadFailed': 'Spisak uređaja nije moguće učitati.',
  'profile.sessions.empty': 'Nema aktivnih prijava.',
  'profile.sessions.thisDevice': 'ovaj uređaj',
  'profile.sessions.lastActivity': 'Posljednja aktivnost {when}',
  'profile.sessions.neverUsed': 'Još nije korišten',
  'profile.sessions.logout': 'Odjavi',
  'profile.sessions.logoutSelf': 'Odjavi se',
  'profile.sessions.revokeAria': 'Odjavi {device}',
  'profile.sessions.logoutFailed': 'Odjava nije uspjela.',

  // ---- Forgotten password ------------------------------------------
  /*
   * "Ako nalog postoji" has to stay that awkward: the employee code is
   * printed on badges all over the plant, so an answer that distinguished a
   * real code from an invented one would enumerate the payroll.
   */
  'profile.forgot.title': 'Zaboravljena lozinka',
  'profile.forgot.intro':
    'Upišite svoju šifru zaposlenog. Ako na nalogu imate potvrđenu e-mail adresu, poslaćemo vam link.',
  'profile.forgot.identifier': 'Šifra zaposlenog ili e-mail',
  'profile.forgot.identifier.hint': 'Bilo koje od to dvoje — šta god vam je pri ruci.',
  'profile.forgot.identifier.placeholder': 'npr. 1042',
  'profile.forgot.submit': 'Pošalji link',
  'profile.forgot.sendFailed': 'Slanje nije uspjelo. Provjerite vezu i pokušajte ponovo.',
  'profile.forgot.sent.title': 'Provjerite e-poštu',
  'profile.forgot.sent.body':
    'Ako nalog postoji i ima potvrđenu e-mail adresu, poslali smo link za postavljanje nove lozinke. Pogledajte i neželjenu poštu.',
  'profile.forgot.sent.expiry': 'Link vrijedi sat vremena i može se iskoristiti samo jednom.',
  'profile.forgot.sent.noMail':
    'Nema poruke? Ako na vašem nalogu nema potvrđene e-mail adrese — a većina radnika je nema — obratite se nadređenom. Dobićete aktivacioni kod i sami postavljate novu lozinku.',
  // Two whole sentences rather than one split one: the first is the heading
  // of the panel and is emphasised, the second is the instruction.
  'profile.forgot.noEmail.title': 'Nemate e-mail adresu?',
  'profile.forgot.noEmail.body':
    'Obratite se nadređenom — dobićete aktivacioni kod, prijavite se njim i odmah postavljate svoju lozinku.',

  // ---- Setting a password from a mailed link ------------------------
  // One screen, two occasions: an invitation and a reset are the same act.
  'profile.reset.welcome': 'Dobrodošli',
  'profile.reset.title': 'Nova lozinka',
  'profile.reset.intro.invitation':
    'Postavite lozinku za nalog {code}. Tom šifrom se ubuduće prijavljujete — ne e-mailom.',
  'profile.reset.intro.reset':
    'Postavljate novu lozinku za nalog {code}. Znaćete je samo vi.',
  'profile.reset.submit': 'Sačuvaj novu lozinku',
  'profile.reset.allSessionsNote': 'Nakon promjene sve prijave na svim uređajima se prekidaju.',
  'profile.reset.success': 'Lozinka je promijenjena. Prijavite se novom lozinkom.',
  'profile.reset.failed': 'Promjena lozinke nije uspjela. Provjerite vezu i pokušajte ponovo.',
  'profile.reset.badLink.title': 'Link nije ispravan',
  'profile.reset.badLink.body':
    'Ovaj link je nepotpun — najčešće zato što se prelomio u e-poruci. Zatražite novi i otvorite ga jednim klikom iz poruke.',
  'profile.reset.badLink.action': 'Zatraži novi link',

  // ---- The forced first password -----------------------------------
  // Shown to somebody who signed in with a code another person read out to
  // them, and until it is replaced that person can sign in as them.
  'profile.setPassword.title': 'Postavite svoju lozinku',
  'profile.setPassword.intro':
    'Prijavili ste se aktivacionim kodom koji vam je saopštio nadređeni. Postavite sada lozinku koju znate samo vi — kod nakon toga prestaje da važi.',
  'profile.setPassword.submit': 'Sačuvaj lozinku',
  'profile.setPassword.success': 'Lozinka je postavljena. Dobrodošli.',
  'profile.setPassword.failed': 'Postavljanje lozinke nije uspjelo.',
  'profile.setPassword.signedInAs': 'Prijavljeni ste kao {name} ({code}).',
  'profile.setPassword.notMe': 'Nisam ja — odjavi me',

  // ---- Where a scanned card lands ----------------------------------
  'profile.badgeLogin.working': 'Prijava karticom…',
  'profile.badgeLogin.recognising': 'Prepoznajemo vašu karticu.',
  'profile.badgeLogin.failed': 'Prijava karticom nije uspjela.',
  'profile.badgeLogin.lostCard':
    'Ako je kartica oštećena ili izgubljena, nadređeni vam može izdati novu.',
  'profile.badgeLogin.usePassword': 'Prijava šifrom i lozinkom',

  'profile.twoFactor.required':
    'Vaša firma traži dvofaktorsku zaštitu za administratore. Dok je ne uključite, administracija je zaključana.',
} as const

export type ProfileSlice = Record<keyof typeof profile, string>
